import { type Ref, onUnmounted, ref, watch } from 'vue'
import { createVideoPosterFile } from '@/modules/chat-matrix-fast/utils/videoPoster'

function filePreviewKey(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`
}

/**
 * Blob-URL превью на File через WeakMap — createObjectURL из шаблона
 * на каждый рендер создаёт новый URL. Для видео — JPEG первого кадра.
 */
export function useFilePreviewUrls(files: Ref<File[]>) {
  const previewUrlByFile = new WeakMap<File, string>()
  const videoPosterUrlByKey = ref(new Map<string, string>())
  const videoPosterPending = new Set<string>()

  function getPreviewUrl(file: File): string | null {
    if (typeof window === 'undefined' || !window.URL?.createObjectURL) {
      return null
    }

    const cached = previewUrlByFile.get(file)
    if (cached) {
      return cached
    }

    const url = window.URL.createObjectURL(file)
    previewUrlByFile.set(file, url)

    return url
  }

  function getVideoPosterUrl(file: File): string | null {
    return videoPosterUrlByKey.value.get(filePreviewKey(file)) || null
  }

  async function ensureVideoPoster(file: File): Promise<void> {
    if (!file.type.startsWith('video/')) {
      return
    }
    const key = filePreviewKey(file)
    if (videoPosterUrlByKey.value.has(key) || videoPosterPending.has(key)) {
      return
    }
    videoPosterPending.add(key)
    try {
      const poster = await createVideoPosterFile(file)
      if (!poster) {
        return
      }
      const url = URL.createObjectURL(poster)
      const next = new Map(videoPosterUrlByKey.value)
      next.set(key, url)
      videoPosterUrlByKey.value = next
    } finally {
      videoPosterPending.delete(key)
    }
  }

  function revokePreviewUrl(file: File): void {
    const url = previewUrlByFile.get(file)
    if (url) {
      window.URL.revokeObjectURL(url)
      previewUrlByFile.delete(file)
    }

    const key = filePreviewKey(file)
    const posterUrl = videoPosterUrlByKey.value.get(key)
    if (posterUrl) {
      window.URL.revokeObjectURL(posterUrl)
      const next = new Map(videoPosterUrlByKey.value)
      next.delete(key)
      videoPosterUrlByKey.value = next
    }
    videoPosterPending.delete(key)
  }

  function revokeAllPreviewUrls(list: File[]): void {
    for (const file of list) {
      revokePreviewUrl(file)
    }
    for (const url of videoPosterUrlByKey.value.values()) {
      window.URL.revokeObjectURL(url)
    }
    videoPosterUrlByKey.value = new Map()
    videoPosterPending.clear()
  }

  watch(
    files,
    list => {
      for (const file of list) {
        if (file.type.startsWith('video/')) {
          void ensureVideoPoster(file)
        }
      }
    },
    { deep: true },
  )

  onUnmounted(() => {
    revokeAllPreviewUrls(files.value)
  })

  return {
    getPreviewUrl,
    getVideoPosterUrl,
    revokePreviewUrl,
    revokeAllPreviewUrls,
  }
}
